import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { doFetch } from '../redux/api/actions';
import { GET_TOURS } from '../redux/api/constants';
import api_endpoint from '../settings';
import { useTranslation } from 'react-i18next';
import './MapAddTrackModal.css';

const categoryTranslationKeys = {
  "General": "category-general",
  "Ghost Tour": "category-ghost-tour",
  "Historical": "category-historical",
  "Pop Culture": "category-pop-culture",
  "Family History": "category-family-history",
  "Food & Drink": "category-food-drink",
  "Architecture": "category-architecture",
  "Nature & Parks": "category-nature-parks",
  "Art & Culture": "category-art-culture",
};

const categoryOptions = Object.keys(categoryTranslationKeys);

const MapAddTrackModal = ({ isOpen, onClose, latitude, longitude, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accessToken = useSelector(state => state.Auth.accessToken);
  const tours = useSelector(state => state.API.tours);

  const [selectedTourId, setSelectedTourId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [textContent, setTextContent] = useState('');
  const [sequenceOrder, setSequenceOrder] = useState(1);
  const [audioFile, setAudioFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  // New tour creation state
  const [creatingNewTour, setCreatingNewTour] = useState(false);
  const [newTourTitle, setNewTourTitle] = useState('');
  const [newTourDescription, setNewTourDescription] = useState('');
  const [newTourCategory, setNewTourCategory] = useState('General');

  // Fetch user's tours on mount
  useEffect(() => {
    if (isOpen && accessToken) {
      dispatch(doFetch(GET_TOURS, 'tourguide/tours', accessToken));
    }
  }, [isOpen, accessToken, dispatch]);

  // Extract duration from audio file using Web Audio API
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAudioFile(file);
    setError(null);

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const durationSeconds = Math.floor(audioBuffer.duration);
      setDuration(durationSeconds);
    } catch (err) {
      console.error('Error reading audio file:', err);
      setError(t('track-error-read-audio-file'));
    }
  };

  const handleCreateNewTour = async () => {
    if (!newTourTitle.trim()) {
      setError(t('track-error-enter-tour-title'));
      return;
    }

    setUploading(true);
    try {
      const response = await fetch(
        `${api_endpoint}/tourguide/tours`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: newTourTitle.trim(),
            description: newTourDescription.trim(),
            category: newTourCategory,
            privacy_level: 'PUBLIC'
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('track-error-create-tour'));
      }

      const newTour = await response.json();

      // Refresh tours list
      dispatch(doFetch(GET_TOURS, 'tourguide/tours', accessToken));

      // Select the new tour
      setSelectedTourId(newTour.id);
      setCreatingNewTour(false);
      setNewTourTitle('');
      setNewTourDescription('');
      setNewTourCategory('General');
      setUploading(false);
    } catch (err) {
      console.error('Error creating tour:', err);
      setError(err.message || t('track-error-create-tour'));
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedTourId || selectedTourId === 'new') {
      setError(t('track-error-select-tour'));
      return;
    }

    if (!title.trim()) {
      setError(t('track-error-title'));
      return;
    }

    if (!audioFile && !textContent.trim()) {
      setError(t('track-error-content'));
      return;
    }

    if (audioFile && duration <= 0) {
      setError(t('track-error-duration'));
      return;
    }

    setUploading(true);

    try {
      let uploadedAudioUrl = null;

      // Step 1: Upload audio file if provided
      if (audioFile) {
        const presignedResponse = await fetch(
          `${api_endpoint}/tourguide/tours/${selectedTourId}/tracks/presigned-url`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              filename: audioFile.name,
              content_type: audioFile.type || 'application/octet-stream'
            })
          }
        );

        if (!presignedResponse.ok) {
          throw new Error(t('track-error-get-upload-url'));
        }

        const presignedData = await presignedResponse.json();

        // Upload file to S3
        const uploadResponse = await fetch(presignedData.presigned_url, {
          method: 'PUT',
          body: audioFile,
          headers: {
            'Content-Type': audioFile.type || 'application/octet-stream'
          }
        });
        if (!uploadResponse.ok) {
          throw new Error(t('track-error-upload-audio'));
        }

        uploadedAudioUrl = presignedData.audio_url;
      }

      // Step 2: Create track record
      const trackPayload = {
        title: title.trim(),
        description: description.trim(),
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        sequence_order: parseInt(sequenceOrder)
      };

      if (audioFile) {
        trackPayload.duration_seconds = duration;
        trackPayload.filename = audioFile.name;
        trackPayload.audio_url = uploadedAudioUrl;
      }

      if (textContent.trim()) {
        trackPayload.text_content = textContent.trim();
      }

      const createResponse = await fetch(
        `${api_endpoint}/tourguide/tours/${selectedTourId}/tracks`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(trackPayload)
        }
      );

      if (!createResponse.ok) {
        throw new Error(t('track-error-create-stop'));
      }

      const trackData = await createResponse.json();

      // Success!
      setUploading(false);
      onSuccess(trackData);
      handleClose();
    } catch (err) {
      console.error('Error uploading track:', err);
      setError(err.message || t('track-error-upload-track'));
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedTourId('');
    setTitle('');
    setDescription('');
    setTextContent('');
    setSequenceOrder(1);
    setAudioFile(null);
    setDuration(0);
    setError(null);
    setUploading(false);
    setCreatingNewTour(false);
    setNewTourTitle('');
    setNewTourDescription('');
    setNewTourCategory('General');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content map-add-track-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('track-add-stop')}</h2>
          <button className="modal-close" onClick={handleClose} disabled={uploading}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('track-tour')}</label>
            <select
              value={selectedTourId}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'new') {
                  setCreatingNewTour(true);
                  setSelectedTourId('');
                } else {
                  setSelectedTourId(value);
                  setCreatingNewTour(false);
                }
              }}
              required
              disabled={uploading}
            >
              <option value="">{t('track-select-tour-placeholder')}</option>
              {tours.map(tour => (
                <option key={tour.id} value={tour.id}>
                  {tour.title}
                </option>
              ))}
              <option value="new">{t('track-create-new-tour')}</option>
            </select>
          </div>

          {/* New Tour Creation Form */}
          {creatingNewTour && (
            <div className="new-tour-form">
              <div className="form-group">
                <label>{t('track-new-tour-title')}</label>
                <input
                  type="text"
                  value={newTourTitle}
                  onChange={(e) => setNewTourTitle(e.target.value)}
                  placeholder={t('track-tour-title-placeholder')}
                  disabled={uploading}
                />
              </div>
              <div className="form-group">
                <label>{t('track-new-tour-description')}</label>
                <textarea
                  value={newTourDescription}
                  onChange={(e) => setNewTourDescription(e.target.value)}
                  placeholder={t('track-new-tour-description-placeholder')}
                  rows="2"
                  disabled={uploading}
                />
              </div>
              <div className="form-group">
                <label>{t('tour-category')}</label>
                <select
                  value={newTourCategory}
                  onChange={(e) => setNewTourCategory(e.target.value)}
                  disabled={uploading}
                >
                  {categoryOptions.map(option => (
                    <option key={option} value={option}>{t(categoryTranslationKeys[option])}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={handleCreateNewTour}
                disabled={uploading || !newTourTitle.trim()}
                className="btn-primary"
                style={{ marginBottom: '16px' }}
              >
                {t('track-create-tour')}
              </button>
            </div>
          )}

          <div className="form-group">
            <label>{t('track-location')}</label>
            <div className="location-display">
              <span>📍 {latitude.toFixed(6)}, {longitude.toFixed(6)}</span>
            </div>
          </div>

          <div className="form-group">
            <label>{t('track-title')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('track-title-placeholder')}
              required
              disabled={uploading}
            />
          </div>

          <div className="form-group">
            <label>{t('track-text-content')}</label>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder={t('track-text-content-full-placeholder')}
              rows="4"
              disabled={uploading}
            />
            <small>{t('track-provide-content-help')}</small>
          </div>

          <div className="form-group">
            <label>{t('track-audio-file-optional')}</label>
            <input
              type="file"
              accept="audio/*"
              onChange={handleFileChange}
              disabled={uploading}
            />
            {duration > 0 && (
              <small className="duration-info">{t('track-duration')} {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}</small>
            )}
          </div>

          <div className="form-group">
            <label>{t('track-additional-notes')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('track-additional-notes-placeholder')}
              rows="2"
              disabled={uploading}
            />
          </div>

          <div className="form-group">
            <label>{t('track-sequence-order')}</label>
            <input
              type="number"
              value={sequenceOrder}
              onChange={(e) => setSequenceOrder(e.target.value)}
              min="1"
              disabled={uploading}
            />
            <small>{t('track-sequence-help-one')}</small>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={handleClose} disabled={uploading} className="btn-secondary">
              {t('cancel')}
            </button>
            <button type="submit" disabled={uploading} className="btn-primary">
              {uploading ? t('track-saving') : t('track-add-stop')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MapAddTrackModal;
